import React, { useEffect, useMemo } from "react";
import {
  Card,
  Table,
  Form,
  Badge,
  Button,
  Col,
  Row,
  InputGroup,
} from "react-bootstrap";
import { TreeSelect } from "primereact/treeselect";
import { useFormContext } from "react-hook-form";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";

function findVehicleById(list, id) {
  for (const vehicle of list || []) {
    if (String(vehicle.id) === String(id)) return vehicle;
    if (vehicle.children) {
      const found = findVehicleById(vehicle.children, id);
      if (found) return { ...found, parent: vehicle };
    }
  }
  return null;
}

function CvsTab() {
  const {
    register,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { data: fetchdata, postData: fetchContract } = usePostRequest(
    API_ENDPOINTS.GET_CONTRACT,
  );
  const { postData: fetchCvCount } = usePostRequest(
    API_ENDPOINTS.GET_CV_PRODUCT_COUNT,
  );

  useEffect(() => {
    fetchContract();
  }, []);

  const variants = watch("variants") || [];
  const contractVehicles = watch("contractVehicles") || [];
  const cvsData = watch("cvsData") || {};
  const activeCvId = watch("activeCvId") || null;
  const skuId = watch("skuId");
  const vendorSkuBase = watch("vendor_sku");

  const selectedCvs = contractVehicles.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

  function generateUniqueId10() {
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);

    return btoa(String.fromCharCode(...array))
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 10);
  }

  const effectiveVariants = useMemo(() => {
    if (variants.length > 0) return variants;
    if (!vendorSkuBase && !skuId) return [];
    return [
      {
        internalId: vendorSkuBase || skuId || "",
        variantSku: skuId || vendorSkuBase || "",
      },
    ];
  }, [variants, vendorSkuBase, skuId]);

  const contractVehicleNodes = (fetchdata?.data || []).map((vehicle) => ({
    key: String(vehicle.id),
    label: vehicle.name,
    selectable: true,
    children:
      vehicle.children?.map((child) => ({
        key: String(child.id),
        label: child.name,
        selectable: true,
        children: [],
      })) || [],
  }));

  // CvsTab.jsx — replace the sync useEffect entirely
  // useEffect(() => {
  //   // Wait for the CV list to be ready — generating a row before this
  //   // resolves is exactly what was producing unprefixed CV SKUs.
  //   if (!fetchdata?.data) return;

  //   const currentCvsData = getValues("cvsData") || {};
  //   if (Object.keys(currentCvsData).length === 0) return;

  //   let changed = false;
  //   const updated = {};

  //   Object.entries(currentCvsData).forEach(([key, cv]) => {
  //     const existingRows = cv.rows || [];
  //     const existingByVariantId = new Map(
  //       existingRows.map((row) => [row.variantInternalId, row]),
  //     );

  //     const keptRows = variants
  //       .filter((v) => existingByVariantId.has(v.internalId))
  //       .map((v) => {
  //         const prevRow = existingByVariantId.get(v.internalId);
  //         return { ...prevRow, variantSku: v.variantSku };
  //       });

  //     const newVariants = variants.filter(
  //       (v) => !existingByVariantId.has(v.internalId),
  //     );

  //     const baseCount = cv.baseCount || 0;
  //     let nextClin = keptRows.reduce((max, row) => {
  //       const n = parseInt(row.clinId, 10);
  //       return Number.isNaN(n) ? max : Math.max(max, n);
  //     }, baseCount);

  //     // fetchdata is guaranteed loaded here — no more racing with an
  //     // async fetch that may still be null at run time.
  //     const vehicle = findVehicleById(fetchdata.data, key);
  //     const resolvedLetter = vehicle?.letter || cv.letter || "";
  //     const prefix = resolvedLetter ? `${resolvedLetter}-` : "";

  //     const addedRows = newVariants.map((v) => {
  //       nextClin += 1;
  //       return {
  //         variantInternalId: v.internalId,
  //         variantSku: v.variantSku,
  //         cvSku: `${prefix}${v.internalId}`,
  //         clinId: String(nextClin).padStart(4, "0"),
  //         rowId: generateUniqueId10(),
  //       };
  //     });
  //     // const addedRows = newVariants.map((v) => {
  //     //   nextClin += 1;
  //     //   return {
  //     //     variantInternalId: v.internalId,
  //     //     variantSku: v.variantSku,
  //     //     cvSku: `${prefix}${v.internalId}`,
  //     //     clinId: String(nextClin).padStart(4, "0"),
  //     //   };
  //     // });

  //     const nextRows = [...keptRows, ...addedRows];

  //     const sameLength = nextRows.length === existingRows.length;
  //     const sameOrder =
  //       sameLength &&
  //       nextRows.every(
  //         (row, i) =>
  //           row.variantInternalId === existingRows[i]?.variantInternalId,
  //       );
  //     const letterChanged = resolvedLetter !== cv.letter;
  //     if (!sameLength || !sameOrder || letterChanged) changed = true;

  //     updated[key] = { ...cv, letter: resolvedLetter, rows: nextRows };
  //   });

  //   if (changed) {
  //     setValue("cvsData", updated);
  //   }
  // }, [variants, fetchdata]);

  // CvsTab.jsx — sync useEffect: use effectiveVariants everywhere variants was used
  useEffect(() => {
    if (!fetchdata?.data) return;

    const currentCvsData = getValues("cvsData") || {};
    if (Object.keys(currentCvsData).length === 0) return;

    let changed = false;
    const updated = {};

    Object.entries(currentCvsData).forEach(([key, cv]) => {
      const existingRows = cv.rows || [];
      const existingByVariantId = new Map(
        existingRows.map((row) => [row.variantInternalId, row]),
      );

      const keptRows = effectiveVariants
        .filter((v) => existingByVariantId.has(v.internalId))
        .map((v) => {
          const prevRow = existingByVariantId.get(v.internalId);
          return { ...prevRow, variantSku: v.variantSku };
        });

      const newVariants = effectiveVariants.filter(
        (v) => !existingByVariantId.has(v.internalId),
      );

      const baseCount = cv.baseCount || 0;
      let nextClin = keptRows.reduce((max, row) => {
        const n = parseInt(row.clinId, 10);
        return Number.isNaN(n) ? max : Math.max(max, n);
      }, baseCount);

      const vehicle = findVehicleById(fetchdata.data, key);
      const resolvedLetter = vehicle?.letter || cv.letter || "";
      const prefix = resolvedLetter ? `${resolvedLetter}-` : "";

      const addedRows = newVariants.map((v) => {
        nextClin += 1;
        return {
          variantInternalId: v.internalId,
          variantSku: v.variantSku,
          cvSku: `${prefix}${v.internalId}`,
          clinId: String(nextClin).padStart(4, "0"),
          rowId: generateUniqueId10(),
        };
      });

      const nextRows = [...keptRows, ...addedRows];

      const sameLength = nextRows.length === existingRows.length;
      const sameOrder =
        sameLength &&
        nextRows.every(
          (row, i) =>
            row.variantInternalId === existingRows[i]?.variantInternalId,
        );
      const letterChanged = resolvedLetter !== cv.letter;
      if (!sameLength || !sameOrder || letterChanged) changed = true;

      updated[key] = { ...cv, letter: resolvedLetter, rows: nextRows };
    });

    if (changed) {
      setValue("cvsData", updated);
    }
  }, [effectiveVariants, fetchdata]);

  // CvsTab.jsx — seedRowsForCv, rowId generated per row when a CV is first selected
// CvsTab.jsx — seedRowsForCv: use effectiveVariants
  const seedRowsForCv = (key, baseCvsData) => {
    fetchCvCount({ contract_type_id: key }, (res) => {
      const baseCount = res?.count || 0;
      const vehicle = findVehicleById(fetchdata?.data, key);
      const prefix = vehicle?.letter ? `${vehicle.letter}-` : "";
      const rows = effectiveVariants.map((variant, index) => ({
        variantInternalId: variant.internalId,
        variantSku: variant.variantSku,
        cvSku: `${prefix}${variant.internalId}`,
        clinId: String(baseCount + index + 1).padStart(4, "0"),
        rowId: generateUniqueId10(),
      }));
      setValue(`cvsData.${key}`, {
        id: key,
        name: vehicle?.name,
        parentName: vehicle?.parent?.name,
        letter: vehicle?.letter || "",
        rows,
        baseCount,
        baseCountSynced: true,
      });
    });
  };

  // const seedRowsForCv = (key, baseCvsData) => {
  //   fetchCvCount({ contract_type_id: key }, (res) => {
  //     const baseCount = res?.count || 0;
  //     const rows = variants.map((variant, index) => ({
  //       variantInternalId: variant.internalId,
  //       variantSku: variant.variantSku,
  //       cvSku: variant.internalId,
  //       clinId: String(baseCount + index + 1).padStart(4, "0"),
  //     }));
  //     const vehicle = findVehicleById(fetchdata?.data, key);
  //     setValue(`cvsData.${key}`, {
  //       id: key,
  //       name: vehicle?.name,
  //       parentName: vehicle?.parent?.name,
  //       rows,
  //       baseCount,
  //       baseCountSynced: true,
  //     });
  //   });
  // };

  useEffect(() => {
    contractVehicles.forEach((key) => {
      const cv = cvsData[key];
      if (!cv || cv.baseCountSynced) return;

      fetchCvCount({ contract_type_id: key }, (res) => {
        const apiCount = res?.count || 0;
        const latestCvsData = getValues("cvsData") || {};
        const latestCv = latestCvsData[key];
        if (!latestCv) return;

        const localMaxClin = (latestCv.rows || []).reduce((max, row) => {
          const n = parseInt(row.clinId, 10);
          return Number.isNaN(n) ? max : Math.max(max, n);
        }, 0);

        setValue("cvsData", {
          ...latestCvsData,
          [key]: {
            ...latestCv,
            baseCount: Math.max(localMaxClin, apiCount),
            baseCountSynced: true,
          },
        });
      });
    });
  }, [contractVehicles, cvsData]);

  // const seedRowsForCv = (key, baseCvsData) => {
  //   fetchCvCount({ contract_type_id: key }, (res) => {
  //     const baseCount = res?.count || 0;
  //     const rows = variants.map((variant, index) => ({
  //       variantInternalId: variant.internalId,
  //       variantSku: variant.variantSku,
  //       cvSku: variant.internalId,
  //       clinId: String(baseCount + index + 1).padStart(4, "0"),
  //     }));
  //     // const rows = variants.map((variant, index) => ({
  //     //   variantSku: variant.variantSku,
  //     //   internalId: variant.internalId,
  //     //   clinId: String(baseCount + index + 1).padStart(4, "0"),
  //     // }));
  //     const vehicle = findVehicleById(fetchdata?.data, key);
  //     setValue(`cvsData.${key}`, {
  //       id: key,
  //       name: vehicle?.name,
  //       parentName: vehicle?.parent?.name,
  //       rows,
  //     });
  //   });
  // };

  const handleCvDropdownChange = (e) => {
    const newKeys = Object.keys(e.value || {});
    setValue("contractVehicles", newKeys);

    const prunedCvsData = {};
    newKeys.forEach((key) => {
      if (cvsData[key]) prunedCvsData[key] = cvsData[key];
    });
    setValue("cvsData", prunedCvsData);

    // Seed rows once for every newly selected CV that doesn't have data yet.
    newKeys.forEach((key) => {
      if (!prunedCvsData[key]) {
        seedRowsForCv(key, prunedCvsData);
      }
    });

    if (!newKeys.includes(activeCvId)) {
      setValue("activeCvId", newKeys[0] || null);
    }
  };

  const handleCvSkuChange = (index, value) => {
    const current = cvsData[activeCvId];
    if (!current) return;
    const updatedRows = current.rows.map((row, i) =>
      i === index ? { ...row, cvSku: value } : row,
    );
    setValue("cvsData", {
      ...cvsData,
      [activeCvId]: { ...current, rows: updatedRows },
    });
  };

  const handleClinChange = (index, value) => {
    const current = cvsData[activeCvId];
    if (!current) return;
    const updatedRows = current.rows.map((row, i) =>
      i === index ? { ...row, clinId: value } : row,
    );
    setValue("cvsData", {
      ...cvsData,
      [activeCvId]: { ...current, rows: updatedRows },
    });
  };

  // Just switches which table is shown — no API call, no regeneration,
  // so anything typed into CV SKU is preserved when hopping between CVs.
  const handleSelectCvLabel = (key) => {
    setValue("activeCvId", key);
  };

  const activeCv = cvsData[activeCvId];

  return (
    <div>
      <Row className="mb-3">
        <Col lg={4} md={4} xs={6}>
          <Form.Group>
            <Form.Label className="required">Contract Vehicle</Form.Label>
            <InputGroup>
              <TreeSelect
                value={selectedCvs}
                onChange={handleCvDropdownChange}
                options={contractVehicleNodes}
                selectionMode="multiple"
                placeholder="Select contract vehicles"
                filter
                filterPlaceholder="Search contract vehicle"
                style={{ width: "100%" }}
              />
            </InputGroup>
            {errors.contractVehicles && (
              <span className="text-danger">
                {errors.contractVehicles.message}
              </span>
            )}
          </Form.Group>
        </Col>
      </Row>

      {contractVehicles.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {contractVehicles.map((key) => {
            const vehicle = findVehicleById(fetchdata?.data, key);
            if (!vehicle) return null;
            const isActive = key === activeCvId;
            return (
              <Button
                key={key}
                variant={isActive ? "dark" : "outline-secondary"}
                onClick={() => handleSelectCvLabel(key)}
              >
                {vehicle.parent?.name && (
                  <Badge bg="light" text="dark" className="me-2">
                    {vehicle.parent.name}
                  </Badge>
                )}
                {vehicle.name}
              </Button>
            );
          })}
        </div>
      )}

      {activeCv && (
        <Card className="mb-3">
          <Card.Body>
            <div className="table-responsive">
              <Table>
                <thead>
                  <tr>
                    <th>VARIANT</th>
                    <th>CV SKU</th>
                    <th>CLIN ID</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCv.rows.map((row, index) => (
                    <tr key={`${activeCvId}-${index}`}>
                      <td>{row.variantSku}</td>
                      <td>
                        <Form.Control
                          type="text"
                          value={row.cvSku}
                          defaultValue={row.internalId}
                          onChange={(e) =>
                            handleCvSkuChange(index, e.target.value)
                          }
                        />
                        <input type="hidden" value={row.rowId || ""} readOnly />
                      </td>
                      {/* <td>
                        <Form.Control
                          type="text"
                          {...register(
                            `cvsData.${activeCvId}.rows.${index}.internalId`,
                          )}
                          defaultValue={row.internalId}
                        />
                      </td> */}
                      <td>
                        <Form.Control
                          type="text"
                          value={row.clinId}
                          onChange={(e) =>
                            handleClinChange(index, e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default CvsTab;
